import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useAnimationControls, useMotionValue, useSpring, useTransform } from "framer-motion";
import { prefersReducedMotion } from "./settings";
import { emotionToSprite, spritesFromBase, personalityFromSrc } from "./characters";
import { ParticleBurst } from "./Particles";

// ── Kingdom Animation Engine · AnimatedCharacter ──────────────────────────────
// A procedurally-rigged storybook character: emotion-driven body language, a
// gentle life-loop (breathing + occasional blink), one-shot reactions (wave,
// jump, celebrate…), smooth expression cross-fades between sprite variants,
// optional auto-blinking (blinkSrc), and an optional subtle pointer-follow lean.
// Fully reduced-motion aware. Reusable everywhere in the Kingdom.

function idleFor(emotion, p) {
  const b = p.bounce, spd = p.speed;
  const grp = emotionToSprite(emotion);
  if (grp === "joy") {
    const fast = emotion === "excited" || emotion === "celebrate";
    return { y: [0, -14 * b, 0], rotate: [0, 2.5, -2.5, 0], scaleY: [1, 1.03, 0.98, 1],
      transition: { duration: (fast ? 0.7 : 1.0) / spd, repeat: Infinity, ease: "easeInOut" } };
  }
  if (grp === "sad") {
    if (emotion === "scared") return { x: [0, -2.5, 2.5, 0], transition: { duration: 0.5, repeat: Infinity } };
    if (emotion === "worried" || emotion === "frustrated" || emotion === "angry" || emotion === "jealous")
      return { x: [0, -2, 2, 0], rotate: [0, -1, 1, 0], transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" } };
    return { y: [0, 3, 0], scaleY: [1, 0.985, 1], transition: { duration: 3.6, repeat: Infinity, ease: "easeInOut" } };
  }
  if (emotion === "proud") return { scale: [1, 1.03, 1], y: [0, -3, 0], transition: { duration: 2.6, repeat: Infinity, ease: "easeInOut" } };
  // neutral / happy default — breathe + a gentle weight shift so idle feels alive
  return { y: [0, -5 * b, 0, 0, 0], scaleY: [1, 1, 1, 0.985, 1], rotate: [0, 0.5, 0, -0.5, 0],
    transition: { duration: 4.2 / spd, repeat: Infinity, ease: "easeInOut", times: [0, 0.3, 0.6, 0.8, 1] } };
}

function actionFor(action, p) {
  const b = p.bounce, sq = p.squish;
  switch (action) {
    case "wave": return { rotate: [0, -9, 9, -7, 5, 0], transition: { duration: 1.1, ease: "easeInOut" } };
    case "greet": return { rotate: [0, -3.5, 3, -2.5, 1.5, 0], y: [0, -6, 0], transition: { duration: 1.3, ease: "easeInOut" } };
    case "jump": return { y: [0, -44 * b, 0], scaleX: [1, 0.94, 1.06 * sq, 1], scaleY: [1, 1.08 * sq, 0.9, 1], transition: { duration: 0.75, ease: "easeOut" } };
    case "celebrate": return { y: [0, -34 * b, 0, -14, 0], rotate: [0, -8, 8, -4, 0], scale: [1, 1.1, 1, 1.04, 1], transition: { duration: 1.1, ease: "easeInOut" } };
    case "nod": return { rotate: [0, 7, 0], transition: { duration: 0.5 } };
    case "shake": return { x: [0, -7, 7, -5, 0], transition: { duration: 0.5 } };
    case "pop": return { scale: [1, 1.16 * sq, 1], transition: { duration: 0.4, ease: "easeOut" } };
    case "transform": return { scale: [1, 0.85, 1.12 * sq, 1], rotate: [0, -4, 4, 0], transition: { duration: 0.9, ease: "easeInOut" } };
    default: return null;
  }
}

export default function AnimatedCharacter({
  src, sprites, expressive = false, emotion = "idle", action, actionKey,
  burst, burstKey, groundShadow = false, blinkSrc, followPointer = false,
  alt = "", className = "", style = {}, testid,
}) {
  const rm = prefersReducedMotion();
  const p = personalityFromSrc(src || (sprites && sprites.neutral));
  const set = sprites || (expressive ? spritesFromBase(src) : { neutral: src, sad: src, joy: src });
  const spriteKey = emotionToSprite(emotion);
  const currentSrc = set[spriteKey] || set.neutral || src;

  // One-shot action controls (defensive against mount/unmount races).
  const controls = useAnimationControls();
  const mountedRef = useRef(false);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => {
    if (rm || !action || !mountedRef.current) return;
    const seq = actionFor(action, p);
    if (!seq) return;
    let cancelled = false;
    const rest = { x: 0, y: 0, rotate: 0, scale: 1, scaleX: 1, scaleY: 1 };
    try {
      const anim = controls.start(seq);
      if (anim && typeof anim.then === "function") {
        anim.then(() => { if (!cancelled && mountedRef.current) { try { controls.start(rest); } catch (e) {} } }).catch(() => {});
      }
    } catch (e) {}
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, actionKey]);

  // Natural auto-blink (randomized, staggered) via a fading overlay sprite.
  const [blinkOn, setBlinkOn] = useState(false);
  useEffect(() => {
    if (rm || !blinkSrc) return;
    let t1, t2, alive = true;
    const loop = () => {
      const next = 2600 + Math.random() * 3800;
      t1 = setTimeout(() => {
        if (!alive) return;
        setBlinkOn(true);
        const doubleBlink = Math.random() < 0.18;
        t2 = setTimeout(() => { if (alive) { setBlinkOn(false); if (!doubleBlink) loop(); else setTimeout(() => { if (alive) { setBlinkOn(true); setTimeout(() => { if (alive) { setBlinkOn(false); loop(); } }, 120); } }, 140); } }, 130);
      }, next);
    };
    loop();
    return () => { alive = false; clearTimeout(t1); clearTimeout(t2); };
  }, [blinkSrc, rm]);

  // Subtle pointer-follow lean ("looking toward the visitor").
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rot = useSpring(useTransform(mx, [-1, 1], [-4, 4]), { stiffness: 60, damping: 18 });
  const tx = useSpring(useTransform(mx, [-1, 1], [-7, 7]), { stiffness: 60, damping: 18 });
  const ty = useSpring(useTransform(my, [-1, 1], [-4, 4]), { stiffness: 60, damping: 18 });
  useEffect(() => {
    if (!followPointer || rm) return;
    const onMove = (e) => {
      const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
      mx.set(Math.max(-1, Math.min(1, (e.clientX - cx) / cx)));
      my.set(Math.max(-1, Math.min(1, (e.clientY - cy) / cy)));
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [followPointer, rm, mx, my]);

  const onImgError = (e) => { if (set.neutral && e.currentTarget.src !== set.neutral) e.currentTarget.src = set.neutral; };

  return (
    <div className={`relative inline-block ${className}`} style={style} data-testid={testid}>
      {groundShadow && !rm && (
        <motion.span aria-hidden className="absolute left-1/2 bottom-0 -translate-x-1/2 rounded-[50%] bg-black/15 blur-[3px]"
          style={{ width: "58%", height: "9%" }}
          animate={{ scaleX: [1, 0.88, 1], opacity: [0.28, 0.2, 0.28] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }} />
      )}
      <motion.div className="w-full h-full" style={followPointer && !rm ? { rotate: rot, x: tx, y: ty, transformOrigin: "50% 100%" } : { transformOrigin: "50% 100%" }}>
        <motion.div className="w-full h-full" style={{ transformOrigin: "50% 100%" }} animate={controls}>
          <motion.div className="relative w-full h-full" style={{ transformOrigin: "50% 100%" }} animate={rm ? {} : idleFor(emotion, p)}>
            <AnimatePresence mode="popLayout">
              <motion.img key={currentSrc} src={currentSrc} alt={alt} draggable={false} onError={onImgError}
                className="w-full h-full object-contain select-none"
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98, position: "absolute", inset: 0 }}
                transition={{ duration: rm ? 0.15 : 0.45, ease: "easeOut" }} />
            </AnimatePresence>
            {blinkSrc && !rm && (
              <motion.img src={blinkSrc} alt="" aria-hidden draggable={false}
                className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                animate={{ opacity: blinkOn ? 1 : 0 }} transition={{ duration: 0.07, ease: "easeInOut" }} />
            )}
          </motion.div>
        </motion.div>
      </motion.div>
      {burst && <ParticleBurst key={burstKey} type={burst} count={16} />}
    </div>
  );
}
